import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Button, Input, Card, Modal } from '../components/common'
import { apiService } from '../services/api'
import { ClipboardList, Search, Eye, Download, Trash2, ArrowLeft, FileSpreadsheet } from 'lucide-react'

interface Submission {
  id: number
  form_id: number
  form_name?: string
  data: Record<string, any>
  created_at: string
  ip_address?: string
}

export default function FormSubmissions() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const formId = searchParams.get('formId')

  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  const fetchSubmissions = async () => {
    try {
      setLoading(true)
      let response
      if (formId) {
        response = await apiService.getFormSubmissions(parseInt(formId))
      } else {
        // For now, show empty list when no formId is provided
        // This can be extended to fetch all submissions from all forms
        setSubmissions([])
        setLoading(false)
        return
      }

      if (response.data && Array.isArray(response.data)) {
        setSubmissions(response.data)
      } else if (response.data && 'data' in response.data && Array.isArray(response.data.data)) {
        setSubmissions(response.data.data)
      } else {
        setSubmissions([])
      }
    } catch (error) {
      setToast({ message: 'Failed to fetch submissions', type: 'error' })
      setSubmissions([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSubmissions()
  }, [searchQuery, formId])

  const handleViewDetail = (submission: Submission) => {
    setSelectedSubmission(submission)
    setIsDetailModalOpen(true)
  }

  const handleDelete = async (submissionId: number) => {
    if (!confirm('Are you sure you want to delete this submission?')) return

    try {
      // Note: deleteSubmission method is not available in apiService
      // Using deleteOrder as a placeholder - this should be implemented in the backend
      await apiService.deleteOrder(submissionId)
      setToast({ message: 'Submission deleted successfully', type: 'success' })
      fetchSubmissions()
    } catch (error) {
      setToast({ message: 'Failed to delete submission', type: 'error' })
    }
  }

  const handleExportCSV = () => {
    if (submissions.length === 0) {
      setToast({ message: 'No submissions to export', type: 'error' })
      return
    }

    // Get all unique field names from submissions
    const allFields = new Set<string>()
    submissions.forEach(sub => {
      Object.keys(sub.data).forEach(key => allFields.add(key))
    })
    const fields = Array.from(allFields)

    // Create CSV header
    const headers = ['ID', 'Form', 'Submitted At', 'IP Address', ...fields]

    // Create CSV rows
    const rows = submissions.map(sub => [
      sub.id,
      sub.form_name || `Form #${sub.form_id}`,
      sub.created_at ? new Date(sub.created_at).toLocaleString() : '-',
      sub.ip_address || '',
      ...fields.map(field => {
        const value = sub.data[field]
        if (Array.isArray(value)) return value.join(', ')
        if (typeof value === 'object') return JSON.stringify(value)
        return value || ''
      })
    ])

    // Combine into CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n')

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `submissions_${new Date().toISOString().split('T')[0]}.csv`
    link.click()

    setToast({ message: 'CSV exported successfully', type: 'success' })
  }

  const handleGoBack = () => {
    navigate('/dashboard/forms')
  }

  return (
    <div className="p-6 space-y-6">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          <div className="flex items-center gap-2">
            <span>{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-2">×</button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={handleGoBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Form Submissions</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              {formId ? `Viewing submissions for Form #${formId}` : 'View all form submissions'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportCSV} className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <ClipboardList className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Submissions</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{submissions.length}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
              <FileSpreadsheet className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Today</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {submissions.filter(s => {
                  const today = new Date().toDateString()
                  const subDate = new Date(s.created_at).toDateString()
                  return today === subDate
                }).length}
              </p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
              <Eye className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">This Week</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {submissions.filter(s => {
                  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                  return new Date(s.created_at) >= weekAgo
                }).length}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <Input
          type="text"
          placeholder="Search submissions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Submissions Table */}
      <Card>
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto" />
            <p className="mt-4 text-gray-500 dark:text-gray-400">Loading submissions...</p>
          </div>
        ) : submissions.length === 0 ? (
          <div className="p-8 text-center">
            <ClipboardList className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No submissions yet</h3>
            <p className="text-gray-500 dark:text-gray-400">Submissions will appear here when users submit your forms</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Form</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Submitted</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">IP Address</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {submissions.map((submission) => (
                  <motion.tr
                    key={submission.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      #{submission.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {submission.form_name || `Form #${submission.form_id}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {submission.created_at ? new Date(submission.created_at).toLocaleString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {submission.ip_address || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetail(submission)}
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(submission.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        title={`Submission #${selectedSubmission?.id}`}
      >
        {selectedSubmission && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400">Form:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{selectedSubmission.form_name || `Form #${selectedSubmission.form_id}`}</span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">Submitted:</span>
                <span className="ml-2 text-gray-900 dark:text-white">
                  {selectedSubmission.created_at ? new Date(selectedSubmission.created_at).toLocaleString() : '-'}
                </span>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400">IP Address:</span>
                <span className="ml-2 text-gray-900 dark:text-white">{selectedSubmission.ip_address || '-'}</span>
              </div>
            </div>

            <div className="border-t dark:border-gray-700 pt-4">
              <h4 className="font-medium text-gray-900 dark:text-white mb-3">Submission Data</h4>
              <div className="space-y-2">
                {Object.entries(selectedSubmission.data).map(([key, value]) => (
                  <div key={key} className="flex justify-between py-2 border-b dark:border-gray-700 last:border-0">
                    <span className="text-gray-500 dark:text-gray-400 capitalize">{key}:</span>
                    <span className="text-gray-900 dark:text-white">
                      {Array.isArray(value) ? value.join(', ') : typeof value === 'object' ? JSON.stringify(value) : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setIsDetailModalOpen(false)}>
                Close
              </Button>
              <Button
                variant="danger"
                onClick={() => {
                  handleDelete(selectedSubmission.id)
                  setIsDetailModalOpen(false)
                }}
              >
                Delete
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
